<template>
  <v-card>
    <!--Form title-->
    <v-card-title class="secondary white--text d-flex align-center justify-space-between">
      <span v-if="!isEditing" class="header-font" style="font-size: 22px;">
        <v-icon style="font-size: 18px;" color="white" class="mr-2">add</v-icon>
        {{$t('scoutingCalendar.tasks.newTask')}}
      </span>
      <span v-else class="header-font" style="font-size: 22px;">
        <v-icon style="font-size: 18px;" color="white" class="mr-2">add</v-icon>
        {{ task.name }}
      </span>
      <span class="monospace-font body-2" v-if="task.start" @click="showSelectStartDate = true" style="cursor: pointer;">
        <v-icon small color="white" class="mr-2">event</v-icon>{{ task.start }}
      </span>
    </v-card-title>

    <v-card-text>
      <v-row class="justify-space-between align-center mt-5">
        <v-col cols="12" class="my-0 py-0">
          <p class="mb-0 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('scoutingCalendar.tasks.taskDetails') }}</p>
        </v-col>
        <!--Task name-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 8" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.taskName') }}</p>
          <v-text-field outlined dense hide-details 
            v-model="task.name" 
            class="body-1 slim-input"
            :rules="requiredRules" 
            :error="nameError"
            @blur="recognizeTimeHour()"
          ></v-text-field>
        </v-col>
        <!--Color-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 2" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.color') }}</p>
          <v-autocomplete hide-details dense outlined
            class="body-1 slim-input"
            v-model="task.color"
            :items="colors"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-icon class="me-1" :color="item.name">opacity</v-icon>
            </template>
            <template v-slot:item="{item}">
              <v-icon class="me-1" :color="item.name">opacity</v-icon>
            </template>
          </v-autocomplete>
        </v-col>
        <!--All day-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 2" class="my-0 py-0">
          <v-checkbox class="" color="secondary" dense v-model="allDay" hide-details
            @change="handleAllDayChange()">
            <template v-slot:label>
              <span class="body-1 primary--text">{{ $t('scoutingCalendar.tasks.allDay') }}</span>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row class="align-end mb-3">
        <!--Start date (today by default)-->
        <v-col :cols="handleDateFieldsDisplay()" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.startDate') }}</p>
          <v-menu v-model="showSelectStartDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field readonly v-bind="attrs" v-on="on" hide-details clearable outlined
                v-model="task.start" 
                class="body-1 slim-input"
                :rules="requiredRules" 
                :error="startDateError"
              ></v-text-field>
            </template>
            <v-date-picker no-title color="secondary"
              v-model="task.start" 
              :locale="currentUserLanguage" 
              :first-day-of-week="1" 
              @change="checkEndDateError()"
            >
            </v-date-picker>
          </v-menu>
        </v-col>
        <!--Start Hour (if not all day task)-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 3" v-if="!allDay" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.startHour') }}</p>
          <v-autocomplete hide-details dense clearable outlined
            class="body-1 slim-input"
            v-model="startHour" 
            :items="hourOptions" 
            item-value="value" 
            item-text="label" 
            @change="handleStartHourChange()"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.label}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.label}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--End Hour (if not all day task)-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 3" v-if="!allDay" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.endHour') }}</p>
          <v-autocomplete hide-details dense clearable outlined
            class="body-1 slim-input"
            v-model="endHour" 
            :items="hourOptions" 
            item-value="value" 
            item-text="label" 
            menu-props="auto" 
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.label}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.label}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--End date (same as start date by default)-->
        <v-col :cols="handleDateFieldsDisplay()" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.endDate') }}</p>
          <v-menu v-model="showSelectEndDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field readonly v-bind="attrs" v-on="on" class="body-1 slim-input" hide-details clearable outlined
                v-model="task.end" 
              ></v-text-field>
            </template>
            <v-date-picker no-title color="secondary"
              v-model="task.end" 
              :locale="currentUserLanguage" 
              :first-day-of-week="1" 
              @change="showSelectEndDate = false"
              :allowed-dates="setAllowedEndDates"
            >
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <v-row class="justify-space-between align-center">
        <!--State-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('general.state') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.boardStateId"
            :items="states"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                <v-icon class="me-1" :color="item.color" x-small>trip_origin</v-icon>
                {{item.name}}
              </v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">
                <v-icon class="me-1" :color="item.color" x-small>trip_origin</v-icon>
                {{item.name}}
              </v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Visibility-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('collections.visibility') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.visibilityId"
            :items="visibilities"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                <v-icon class="me-1" x-small>{{ item.icon }}</v-icon>
                {{item.name}}
              </v-card-text>
            </template>
            <template v-slot:item="{item, attrs, on }">
              <v-tooltip right color="lightSecondary lighten-4" max-width="300">
                <template v-slot:activator="{ on: tooltipOn }">
                  <v-card-text class="pa-0 primary--text font-weight-medium body-1" v-bind="attrs" v-on="{ ...on, ...tooltipOn }">
                    <v-icon class="me-1" x-small>{{ item.icon }}</v-icon>
                    {{item.name}}
                  </v-card-text>
                </template>
                <span class="black--text body-1">{{ $t(`collections.privacity.${item.description}`) }}</span>
              </v-tooltip>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Assigned to (only users with equal or lower typeName in this.userTypesHierarchy)-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.assignedTo') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.userAssignedIds"
            :items="usersToBeAssigned"
            item-value="id"
            item-text="label"
            :loading="!usersToBeAssigned.length"
            :disabled="!usersToBeAssigned.length"
            menu-props="auto"
            multiple
          >
            <template v-slot:selection="{item, index}">
              <span v-if="index === 0">{{ item.label }}</span>
              <span v-if="index === 1" class="pa-0 primary--text d-flex align-center ml-2">
                <span class="body-2" >(+{{`${task.userAssignedIds.length - 1} ${$t('finishingMap.more')}`}})</span>
              </span>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">
                <v-icon class="me-1" x-small>person</v-icon>
                {{item.label}}
              </v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <v-row class="justify-start align-center">
        <!--Scouting type-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('scoutingCalendar.tasks.scoutingType') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
            </v-tooltip>
          </p>
          <v-autocomplete hide-details dense outlined
            class="body-1 slim-input"
            v-model="task.typeId"
            :items="types"
            item-value="id"
            item-text="name"
            clearable
            @change="resetScoutingDetails()"
            menu-props="auto"
            :disabled="isEditing"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.name}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.name}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Task location-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.taskLocation') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.locationTypeId"
            :items="locations"
            item-value="id"
            item-text="label"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.label}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.label}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      
      <!--Scouting details-->
      <v-row class="justify-space-between align-center" v-if="task.typeId && task.typeId !== 3">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3"></v-divider>
          <p class="mb-2 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('scoutingCalendar.tasks.scoutingDetails') }}</p>
          <v-tabs grow color="secondary" class="elevation-0 mb-3" v-model="currentTab" height="35" @change="resetScoutingDetails()">
            <v-tab class="body-1 monospace-font">{{ $t('scoutingCalendar.individualScouting') }}</v-tab>
            <v-tab class="body-1 monospace-font">{{ $t('scoutingCalendar.matchScouting') }}</v-tab>
          </v-tabs>
        </v-col>
      </v-row>

      <!--Individual scouting-->
      <v-row v-if="task.typeId && task.typeId !== 3 && currentTab === 0" class="mb-2" align="end">
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 5" class="pt-1">
          <SearchBar
            class="mt-n1"
            placeholder="general.search"
            icon="person_add"
            :dataSet="dataSet"
            :navigate="false"
            :playersResults="7"
            :teamsResults="7"
            :showPlayers="task.typeId === 1"
            :showTeams="task.typeId === 2"
            :showGames="false"
            :showLeagues="false"
            :showMore="false"
            @resultClicked="onResultClicked($event)"
          ></SearchBar>
        </v-col>
        <!--Searchbar Team-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 7" v-if="task.typeId === 2" class="pt-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('general.team') }}</p>
          <v-autocomplete hide-details dense outlined readonly clearable append-icon=""
            class="body-1 slim-input"
            v-model="task.teamId"
            :items="searchbarSelectedTeams"
            item-value="id"
            item-text="text"
            menu-props="auto"
            :disabled="!searchbarSelectedTeams.length"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                <div class="d-flex align-center">
                  <img contain width="20" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.id + '.png'"
                  />
                  <span class="ml-4">{{ item.text }}</span>
                </div>
              </v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">
                <div class="d-flex align-center">
                  <img contain width="20" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.id + '.png'"
                  />
                  <span class="ml-4">{{ item.text }}</span>
                </div>
              </v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Searchbar Player-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 7" v-if="task.typeId === 1" class="d-flex align-end justify-space-between pt-1">
          <div class="pa-0 ma-0 flex-grow-1">
            <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('playerDetail.player') }}</p>
            <v-autocomplete hide-details dense outlined multiple clearable class="body-1 slim-input me-3" menu-props="auto"
              v-model="task.players"
              :items="searchbarSelectedPlayers"
              item-value="id"
              item-text="text"
              :disabled="!searchbarSelectedPlayers.length"
            >
              <template v-slot:selection="{item,index}">
                <v-card class="d-flex align-center" elevation="0" color="transparent">
                  <v-card-text v-if="index === 0" class="pa-0 primary--text font-weight-medium body-1">{{ item.text }}</v-card-text>
                  <span v-if="index === 1" class="pa-0 primary--text d-flex align-center ml-2">
                    <span class="body-2 subheader-font" >(+{{`${task.players.length - 1} ${$t('finishingMap.more')}`}})</span>
                  </span>
                </v-card>
              </template>
              <template v-slot:item="{item}">
                <v-card class="d-flex flex-column my-2" elevation="0" color="transparent">
                  <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                    {{ item.text }}
                    <v-chip class="monospace-font" v-if="item.value.pos && item.value.pos !== 0" style="padding: 7px; margin-left: 4px;" :color="getPositionChip(item.value.pos)" text-color="white" x-small>
                      {{ getPositionCode(item.value.pos) }}
                    </v-chip>
                  </v-card-text>
                  <v-card-subtitle class="pa-0 primary--text font-weight-light" v-if="item.value.tName || item.tName">
                    <div class="d-flex align-center mt-1">
                      <img contain width="14" @error="onImageLoadFailure($event)"
                        :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + (item.value.tId) + '.png'"
                      />
                      <span class="ml-1">{{ item.value.tName || item.tName }}</span>
                    </div>
                  </v-card-subtitle>
                </v-card>
              </template>
            </v-autocomplete>
          </div>
          <AddPlayerModal v-if="!!dataSet" :dataSet="dataSet" @dialogClosed="handleAddPlayerClosed($event)" :key="addPlayerModalKey"></AddPlayerModal>
        </v-col>
      </v-row>

      <!--Scouting in game-->
      <v-row v-if="task.typeId && task.typeId !== 3 && currentTab === 1">
        <!--League-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('playerScout.league') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
            </v-tooltip>
          </p>
          <v-autocomplete hide-details dense outlined clearable
            v-if="!showClientLeagueInput || (task.leagueId && task.leagueId !== 'typedLeague')"
            class="body-1 slim-input"
            :items="clientLeagues"
            v-model="task.leagueId"
            item-text="label"
            item-value="leagueId"
            :placeholder="$t('scoutingCalendar.tasks.selectLeague')"
            :loading="!clientLeagues.length"
            :disabled="!clientLeagues.length || isEditing"
            @change="getNextGames()"
            :search-input.sync="searchLeagueInput"
            menu-props="auto"
          >
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">
                <!--For "add other" option leagueId === typedLeague-->
                <div class="d-flex align-center" v-if="item.leagueId === 'typedLeague'">
                  <v-icon small>add</v-icon>
                  <span class="ml-1 text-ellipsis">{{ item.label }}</span>
                </div>
                <div v-else class="d-flex align-center">
                  <img contain width="20" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/' + item.leagueId + '.png'"
                  />
                  <span class="ml-4">{{ item.label }}</span>
                </div>
              </v-card-text>
            </template>
          </v-autocomplete>
          <!--Type input for client league-->
          <v-text-field outlined dense hide-details
            class="body-1 slim-input"
            v-if="showClientLeagueInput" 
            v-model="task.typedLeague"  
            append-icon="expand_more"
            @click:append="handleShowClientLeagueInput()"
          ></v-text-field>
        </v-col>
        <!--Match-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('documents.match') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="isEditing">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.notEditable') }}</span>
            </v-tooltip>
          </p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.gameId"
            :items="nextMatchDates"
            item-value="id"
            item-text="label"
            :disabled="!task.leagueId || isEditing"
            @change="handleMatchChange()"
            v-if="!showClientMatchInput && (task.leagueId && task.leagueId !== 'typedLeague') && !!nextMatchDates.length"
            menu-props="auto"
            :placeholder="$t('scoutingCalendar.tasks.selectMatch')"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                <div class="d-flex align-center">
                  <img contain width="14" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.homeTeamId + '.png'"
                  />
                  <span class="ml-1 text-ellipsis">{{ item.homeTeamName }}</span>
                  <span class="mx-1">—</span>
                  <img contain width="14" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.awayTeamId + '.png'"
                  />
                  <span class="ml-1 text-ellipsis">{{ item.awayTeamName }}</span>
                </div>
              </v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card class="d-flex flex-column" elevation="0" color="transparent">
                <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                  <div class="d-flex align-center" v-if="item.id === 'typedMatch'">
                    <v-icon small>add</v-icon>
                    <span class="ml-1 text-ellipsis">{{ item.label }}</span>
                  </div>
                  <div v-else class="d-flex align-center">
                    <img contain width="14" @error="onImageLoadFailure($event)"
                      :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.homeTeamId + '.png'"
                    />
                    <span class="ml-1">{{ item.homeTeamName }}</span>
                    <span class="mx-1">—</span>
                    <img contain width="14" @error="onImageLoadFailure($event)"
                      :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.awayTeamId + '.png'"
                    />
                    <span class="ml-1">{{ item.awayTeamName }}</span>
                  </div>
                </v-card-text>
                <v-card-subtitle class="pa-0 primary--text font-weight-light">{{ item.matchDate }}</v-card-subtitle>
              </v-card>
            </template>
          </v-autocomplete>
          <!--Type input for client match-->
          <v-text-field outlined dense hide-details
            class="body-1 slim-input"
            v-if="showClientMatchInput || task.leagueId === 'typedLeague' || !nextMatchDates.length" 
            :append-icon="!!nextMatchDates.length ? 'expand_more' : ''"
            @click:append="handleShowClientMatchInput()"
            v-model="task.typedMatch" 
            :disabled="(!isEditing && !task.leagueId) || isEditing"
          ></v-text-field>
        </v-col>
        <!--Use match day-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6">
          <v-checkbox class="mt-0 text-ellipsis" dense hide-details color="secondary"
            v-model="isMatchDate" 
            @change="handleUseMatchDate()" 
            :disabled="!nextMatchDates.length || !task.gameId || task.gameId === 'typedMatch'"
          >
          <template v-slot:label>
            <span class="body-1 primary--text">{{ $t('scoutingCalendar.tasks.useMatchdate') }}</span>
          </template>
          </v-checkbox>
        </v-col>
        <!--Team-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" v-if="task.typeId === 2" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('general.team') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="!task.gameId">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.selectGame') }}</span>
            </v-tooltip>
          </p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.teamId"
            :items="teams"
            item-value="id"
            item-text="name"
            :disabled="!task.gameId"
            v-if="(!isEditing && task.leagueId !== 'typedLeague' && task.gameId !== 'typedMatch' && !!nextMatchDates.length) || (isEditing && task.teamId)"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                <div class="d-flex align-center">
                  <img contain width="20" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.id + '.png'"
                  />
                  <span class="ml-4">{{ item.name }}</span>
                </div>
              </v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">
                <div class="d-flex align-center">
                  <img contain width="20" @error="onImageLoadFailure($event)"
                    :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.id + '.png'"
                  />
                  <span class="ml-4">{{ item.name }}</span>
                </div>
              </v-card-text>
            </template>
          </v-autocomplete>
          <!--Type input for client team-->
          <v-text-field v-else outlined dense hide-details 
            v-model="task.typedTeam" 
            class="body-1 slim-input"
            :disabled="!isEditing && !task.leagueId"
          ></v-text-field>
        </v-col>
        <!--Player-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" v-if="task.typeId === 1" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('searchbar.players') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.players"
            :items="players"
            item-value="id"
            item-text="name"
            :disabled="!task.gameId || !players.length"
            :loading="task.gameId && !players.length"
            v-if="(!isEditing && task.leagueId !== 'typedLeague' && task.gameId !== 'typedMatch' && !!nextMatchDates.length) || (isEditing && task.players.length)"
            multiple
            menu-props="auto"
          >
            <template v-slot:selection="{item,index}">
              <v-card class="d-flex align-center" elevation="0" color="transparent">
                <v-card-text v-if="index === 0" class="pa-0 primary--text font-weight-medium body-1">{{ item.name }}</v-card-text>
                <span v-if="index === 1" class="pa-0 primary--text d-flex align-center ml-2">
                  <span class="body-2" >(+{{`${task.players.length - 1} ${$t('finishingMap.more')}`}})</span>
                </span>
              </v-card>
            </template>
            <template v-slot:item="{item}">
              <v-card class="d-flex flex-column my-2" elevation="0" color="transparent">
                <v-card-text class="pa-0 primary--text font-weight-medium body-1">
                  {{ item.name }}
                  <v-chip class="monospace-font" v-if="item.positionOrder !== 0" style="padding: 7px; margin-left: 4px;" :color="getPositionChip(item.fieldPositionId)" text-color="white" x-small>
                    {{ getPositionCode(item.fieldPositionId) }}
                  </v-chip>
                </v-card-text>
                <v-card-subtitle class="pa-0 primary--text font-weight-light">
                  <div class="d-flex align-center mt-1">
                    <img contain width="14" @error="onImageLoadFailure($event)"
                      :src="'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + item.teamId + '.png'"
                    />
                    <span class="ml-1">{{ item.teamName }}</span>
                  </div>
                </v-card-subtitle>
              </v-card>
            </template>
          </v-autocomplete>
          <!--Type input for client player-->
          <v-text-field v-else outlined dense hide-details 
            v-model="task.typedPlayers" 
            class="body-1 slim-input"
            :disabled="!isEditing && !task.leagueId"
          ></v-text-field>
        </v-col>
      </v-row>

      <!--Accommodation details-->
      <v-row class="justify-space-between align-center" v-if="task.locationTypeId === 1">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3"></v-divider>
          <p class="mb-2 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('scoutingCalendar.tasks.accommodation.accommodationDetails') }}</p>
        </v-col>
        <!--Stadium-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('scoutingCalendar.tasks.stadium') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="!task.gameId">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.selectGame') }}</span>
            </v-tooltip>
          </p>
          <v-autocomplete hide-details dense readonly outlined 
            v-if="task.stadiumId"  
            class="body-1 slim-input"
            v-model="task.stadiumId" 
            :items="stadiums" 
            item-value="id" 
            item-text="name" 
            :disabled="!task.gameId"
            append-icon=""
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1 text-ellipsis">{{item.name}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium text-ellipsis">{{item.name}}</v-card-text>
            </template>
          </v-autocomplete>
          <v-text-field outlined dense hide-details v-else
            v-model="task.location"  
            class="body-1 slim-input"         
          ></v-text-field>
        </v-col>
        <!--Travel-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.travel') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.travelId"
            :items="travelOptions"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.name}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.name}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Hotel-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.hotel') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.hotelId"
            :items="hotelOptions"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.name}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.name}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
        <!--Ticket-->
        <v-col :cols="$vuetify.breakpoint.xs ? 12 : 6" class="py-1">
          <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('scoutingCalendar.tasks.tickets') }}</p>
          <v-autocomplete hide-details dense outlined clearable
            class="body-1 slim-input"
            v-model="task.ticketsId"
            :items="ticketsOptions"
            item-value="id"
            item-text="name"
            menu-props="auto"
          >
            <template v-slot:selection="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium body-1">{{item.name}}</v-card-text>
            </template>
            <template v-slot:item="{item}">
              <v-card-text class="pa-0 primary--text font-weight-medium">{{item.name}}</v-card-text>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <!--Documents-->
      <v-row class="justify-space-between align-center" v-if="task.typeId && task.typeId !== 3">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3"></v-divider>
          <p class="mb-2 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('playerDetail.documents') }}</p>
        </v-col>
        <v-col cols="12" class="my-0 py-1">
          <p v-if="task.typeId === 2 && currentTab === 0" class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('scoutingCalendar.tasks.teamDocuments') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="currentTab === 0 && task.typeId === 2 && !searchbarSelectedTeams.length">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.selectTeam') }}</span>
            </v-tooltip>
          </p>
          <p v-if="task.typeId === 1 && currentTab === 0" class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('scoutingCalendar.tasks.playerDocuments') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="currentTab === 0 && task.typeId === 1 && !searchbarSelectedPlayers.length">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.selectPlayer') }}</span>
            </v-tooltip>
          </p>
          <p v-if="(task.typeId === 1 || task.typeId === 2) && currentTab === 1" class="body-1 font-weight-bold primary--text mb-0">
            {{ $t('scoutingCalendar.tasks.gameDocuments') }}
            <v-tooltip top color="lightSecondary lighten-4" max-width="300" v-if="currentTab === 1 && !task.gameId && task.gameId !== 'typedMatch'">
              <template v-slot:activator="{ on, attrs }">
                <v-icon class="ms-1" x-small color="secondary" v-on="on" v-bind="attrs">info</v-icon>
              </template>
              <span class="black--text body-1">{{ $t('disabledTooltips.selectGame') }}</span>
            </v-tooltip>
          </p>
        </v-col>
        <!--Files-->
        <v-col cols="12" class="pt-0 pb-1">
          <v-file-input hide-details outlined dense height="25px" prepend-icon multiple small-chips
            v-model="task.files"
            prepend-inner-icon="$file"
            :placeholder="$t('general.uploadFile')"
            ref="mainFileInput"
            :disabled="(currentTab === 1 && !task.gameId && task.gameId !== 'typedMatch') 
            || (currentTab === 0 && task.typeId === 2 && !searchbarSelectedTeams.length) 
            || (currentTab === 0 && task.typeId === 1 && !searchbarSelectedPlayers.length)">
          </v-file-input>
        </v-col>
        <v-col class="py-0 d-flex align-center" cols="12" v-if="eventFiles.length" v-for="(document,index) of filteredFiles" :key="index">
          <v-icon small class="me-1" :color="getColorByFileName(document.name)">file_present</v-icon>
          <span class="mb-0 text-ellipsis pr-4">{{ document.name }}</span>
          <v-btn icon @click="onDownloadClick(document)" x-small>
            <v-icon color="grey lighten-1 mb-0" small>file_download</v-icon>
          </v-btn>
          <v-btn icon @click="onDeleteClick(document)" x-small v-if="currentUser.id === task.createdById">
            <v-icon color="grey lighten-1 mb-0" small>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!--Task Files-->
      <v-row class="justify-space-between align-center mt-2">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3" v-if="!task.typeId || task.typeId === 3"></v-divider>
          <p class="my-0 pa-0 primary--text font-weight-bold body-1">{{ $t('scoutingCalendar.tasks.taskDocuments') }}</p>
        </v-col>
        <v-col cols="12" class="py-1">
          <v-file-input hide-details outlined dense height="25px" prepend-icon multiple small-chips
            v-model="task.taskFiles"
            prepend-inner-icon="$file"
            :placeholder="$t('general.uploadFile')"
            ref="taskFileInput">
          </v-file-input>
        </v-col>
        <v-col class="py-0 d-flex align-center" cols="12" v-if="filteredTaskFiles.length" v-for="(document,index) of filteredTaskFiles" :key="index">
          <v-icon small class="me-1" :color="getColorByFileName(document.name)">file_present</v-icon>
          <span class="mb-0 text-ellipsis pr-4">{{ document.name }}</span>
          <v-btn icon @click="onDownloadTaskDocumentClick(document)" x-small>
            <v-icon color="grey lighten-1 mb-0" small>file_download</v-icon>
          </v-btn>
          <v-btn icon @click="onDeleteClick(document)" x-small v-if="currentUser.id === task.createdById">
            <v-icon color="grey lighten-1 mb-0" small>delete</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!--Expense Files-->
      <v-row class="justify-space-between align-center mt-2">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3"></v-divider>
          <p class="mb-2 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('expenses.expenses') }}</p>
        </v-col>
        <!--Show files when creating-->
        <v-col :key="expensesKey" v-if="task.expenseFiles && task.expenseFiles.length" class="mb-1 py-0" cols="12">
          <div class="d-flex align-center" v-for="(document,index) of task.expenseFiles" :key="index">
            <span class="mb-0 text-ellipsis pr-4">
              <v-icon small class="me-1" :color="getColorByFileName(document.name)">file_present</v-icon>
              {{ document.name }}
              <span class="mb-0 text-ellipsis ml-2 body-2">- {{ document.expenseTypeName }}</span>
            </span>
            <v-btn icon @click="onRemoveExpenseClick(document)" x-small>
              <v-icon color="grey lighten-1 mb-0" small>delete</v-icon>
            </v-btn>
          </div>
        </v-col>
        <!--Show files when updating-->
        <v-col class="py-0 d-flex align-center" cols="12" v-if="filteredExpenseFiles.length" v-for="(document,index) of filteredExpenseFiles" :key="index">
          <v-icon small class="me-1" :color="getColorByFileName(document.name)">file_present</v-icon>
          <span class="mb-0 text-ellipsis pr-4">{{ document.name }}</span>
          <v-btn icon @click="onDownloadTaskDocumentClick(document)" x-small>
            <v-icon color="grey lighten-1 mb-0" small>file_download</v-icon>
          </v-btn>
          <v-btn icon @click="onDeleteFileTaskClick(document)" x-small v-if="currentUser.id === task.createdById">
            <v-icon color="grey lighten-1 mb-0" small>delete</v-icon>
          </v-btn>
        </v-col>
        <!--Add expense-->
        <v-col cols="12">
          <v-btn color="secondary" class="px-4 elevation-0 monospace-font" rounded @click="showAddExpenseForm = !showAddExpenseForm" :disabled="showAddExpenseForm">
            <v-icon class="mr-1">add</v-icon>
            {{$t('expenses.addExpense')}}
          </v-btn>
        </v-col>
        <v-col cols="12" v-if="showAddExpenseForm" class="pt-0">
          <v-form name="expensesForm" ref="expensesFormRef" @submit.prevent="onAddExpenseClick">
            <v-row>
              <v-col :cols="$vuetify.breakpoint.xs ? 12 : 3" class="py-1">
                <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('expenses.expenseType') }}</p>
                <v-autocomplete hide-details dense outlined clearable
                  class="body-1 slim-input"
                  v-model="expense.expenseTypeId"
                  :items="expenseTypes"
                  item-value="id"
                  item-text="label"
                  menu-props="auto"
                  :rules="requiredRules"
                >
                  <template v-slot:selection="{item}">
                    <v-card-text class="pa-0 primary--text font-weight-medium body-1 text-ellipsis w-90">{{item.label}}</v-card-text>
                  </template>
                  <template v-slot:item="{item}">
                    <v-card-text class="pa-0 primary--text font-weight-medium text-ellipsis w-90">{{item.label}}</v-card-text>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col :cols="$vuetify.breakpoint.xs ? 6 : 2" class="py-1"> 
                <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('expenses.amount') }}</p>
                <v-text-field outlined dense hide-details 
                  v-model="expense.expenseAmount" 
                  class="body-1 slim-input"
                  type="number"
                  :min="0"
                ></v-text-field>
              </v-col>
              <v-col :cols="$vuetify.breakpoint.xs ? 6 : 3" class="py-1">
                <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('settings.currency') }}</p>
                <v-autocomplete hide-details dense outlined clearable
                  class="body-1 slim-input"
                  v-model="expense.currencyId"
                  :items="currencies"
                  item-value="currencyId"
                  item-text="name"
                  menu-props="auto"
                  :rules="expense.expenseAmount ? requiredRules : []"
                >
                  <template v-slot:selection="{item}">
                    <v-card-text class="pa-0 primary--text font-weight-medium body-1 text-ellipsis w-90">{{ item.name }}</v-card-text>
                  </template>
                  <template v-slot:item="{item}">
                    <v-card-text class="pa-0 primary--text font-weight-medium text-ellipsis w-90">{{ item.name }}</v-card-text>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col :cols="$vuetify.breakpoint.xs ? 12 : 4" class="py-1">
                <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('expenses.expenseDate') }}</p>
                <v-menu v-model="showSelectExpenseDate" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field readonly v-bind="attrs" v-on="on" dense hide-details clearable outlined
                      v-model="expense.expenseDate" 
                      class="mt-0 pt-0 slim-input"
                    ></v-text-field>
                  </template>
                  <v-date-picker no-title color="secondary"
                    v-model="expense.expenseDate" 
                    :locale="currentUserLanguage" 
                    :first-day-of-week="1" 
                    @change="showSelectStartDate = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" class="pt-0">
                <p class="body-1 font-weight-bold primary--text mb-0">{{ $t('general.uploadFile') }}</p>
                <v-file-input hide-details outlined dense height="25px" prepend-icon small-chips
                  v-model="expense.file"
                  prepend-inner-icon="$file"
                  ref="expenseFileInput"
                  :rules="requiredRules">
                </v-file-input>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
        <v-col class="d-flex align-center justify-end pt-0" v-if="showAddExpenseForm">
          <v-btn small :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" text class="monospace-font elevation-0" @click="handleCancelExpense()">{{$t('general.cancel')}}</v-btn>
          <v-btn small rounded outlined color="primary" class="px-4 monospace-font elevation-0" @click="onAddExpenseClick()" :loading="isSaving">{{$t('general.save')}}</v-btn>
        </v-col>
      </v-row>
      <!--Notes-->
      <v-row class="justify-space-between align-center">
        <v-col cols="12" class="my-0 py-0">
          <v-divider class="my-3"></v-divider>
          <p class="ma-0 pa-0 primary--text header-font" style="font-size: 18px;">{{ $t('general.notes') }}</p>
        </v-col>
        <v-col cols="12">
          <v-textarea outlined hide-details height="100px" class="ma-0" 
            :placeholder="$t('scoutingCalendar.tasks.taskNotes')"
            v-model="task.notes"
          ></v-textarea>
        </v-col>
      </v-row>
    </v-card-text>

    <!--Buttons-->
    <v-card-actions class="pr-6 pb-4 d-flex justify-end">
      <v-btn :color="$vuetify.theme.dark ? 'white' : 'secondary darken-1'" rounded outlined class="elevation-0 monospace-font px-4" @click="handleCloseDialog('cancel')" :disabled="isSaving">{{$t('general.cancel')}}</v-btn>
      <v-btn rounded class="elevation-0 monospace-font white--text px-4" color="primary" @click="onSaveClick()" :loading="isSaving">{{$t('general.save')}}</v-btn>
    </v-card-actions>

    <!--Save changes confirmation-->
    <ConfirmationModal 
      @confirmation="onCancelUpdateConfirmation($event)" 
      ref="confirmationRemoveModal"  
      title="scoutingCalendar.tasks.cancelUpdate" 
      message="scoutingCalendar.tasks.cancelUpdateMessage"
    ></ConfirmationModal>
  </v-card>
</template> 
<script>

import Dictionary from '../../dictionary';
import ClientService from '../../services/client.service';
import GameService from '../../services/game/game.service';
import CalendarService from '../../services/calendar.service';
import ConfirmationModal from '../base/ConfirmationModal.vue';
import { getChipColorByPosition, getPositionCode, getCurrencies } from '../../utils/functions';
import * as moment from 'moment';
import DocumentService from '../../services/document.service';
import userService from '../../services/user.service';
import SearchBar from '../base/SearchBar.vue';
import AddPlayerModal from '../player/AddPlayerModal.vue';

export default {
  name: "ScoutingCalendarAddTask",
  title: "Scouting Calendar Add Task",
  components: { ConfirmationModal, SearchBar, AddPlayerModal },
  props: {
    model: {
      type: Boolean,
      default: true
    },
    date: {
      type: Object,
      default: () => {
        return {};
      }
    },
    addEventKey: {
      type: Number,
      default: 1
    },
    taskToUpdate: {
      type: Object,
      default: () => {
        return {};
      }
    },
    taskToCreate: {
      type: Object,
      default: () => {
        return {};
      }
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    usersToBeAssigned: {
      type: Array,
      default: () => {
        return [];
      }
    },
    eventFiles: {
      type: Array,
      default: () => {
        return [];
      }
    },
    taskFiles: {
      type: Array,
      default: () => {
        return [];
      }
    }
  },
  data() {
    return {
      task: { boardStateId: 1, visibilityId: 2, color: 1, locationTypeId: 2, typeId: 1, players: [], expenseFiles: [] },
      requiredRules: [ (v) => !!v || Dictionary[this.$i18n.locale]["general"]["formErrors"]["required"]],
      nameError: false,
      startDateError: false,
      showSelectStartDate: false,
      showSelectEndDate: false,
      allDay: true,
      isMatchDate: false,
      isSaving: false,
      states: [{ id: 1, name: this.$t('scoutingCalendar.tasks.states.toDo'), color: 'red' }, { id: 2, name: this.$t('scoutingCalendar.tasks.states.inProgress'), color: 'amber' }, { id: 3, name: this.$t('scoutingCalendar.tasks.states.done'), color: 'green' }],
      hourOptions: [],
      startHour: null,
      endHour: null,
      visibilities: [{ id: 2, name: this.$t('collections.privacity.privateTitle'), icon: 'lock', description: 'privateDescription' }, { id: 3, name: this.$t('collections.privacity.supervisedTitle'), icon: 'visibility', description: 'supervisedDescription' }, { id: 1, name: this.$t('collections.privacity.publicTitle'), icon: 'public', description: 'publicDescription' }],
      colors: [{ id: 1, name: 'blue' }, { id: 6, name: 'purple' }, { id: 7, name: 'orange' }, { id: 8, name: 'grey' }, { id: 9, name: 'indigo' }, { id: 10, name: 'teal' }, { id: 11, name: 'light-blue' }, { id: 12, name: 'brown' }],
      types: [{ id: 1, name: this.$t('scoutingCalendar.tasks.type.player')}, { id: 2, name: this.$t('scoutingCalendar.tasks.type.team')}, { id: 3, name: this.$t('scoutingCalendar.tasks.type.other') }],
      clientLeagues: [],
      nextMatchDates: [],
      matchDate: null,
      teams: [],
      players: [],
      selectedMatch: {},
      searchLeagueInput: null,
      locations: [{ id: 1, label: this.$t('scoutingCalendar.tasks.location.stadium') }, { id: 2, label: this.$t('scoutingCalendar.tasks.location.video') }],
      stadiums: [],
      showClientLeagueInput: false,
      showClientMatchInput: false,
      travelOptions: [{ id: 1, name: this.$t('scoutingCalendar.tasks.accommodation.ownArrangement') }, { id: 2, name: this.$t('scoutingCalendar.tasks.accommodation.required') }, { id: 3, name: this.$t('scoutingCalendar.tasks.accommodation.notRequired') }],
      hotelOptions: [{ id: 1, name: this.$t('scoutingCalendar.tasks.accommodation.booked') }, { id: 2, name: this.$t('scoutingCalendar.tasks.accommodation.required') }, { id: 3, name: this.$t('scoutingCalendar.tasks.accommodation.notRequired') }],
      ticketsOptions: [{ id: 1, name: this.$t('scoutingCalendar.tasks.accommodation.booked') }, { id: 2, name: this.$t('scoutingCalendar.tasks.accommodation.required') }, { id: 3, name: this.$t('scoutingCalendar.tasks.accommodation.notRequired') }],
      filteredFiles: [],
      filesToDelete: [],
      dataSet: null,
      searchbarSelectedPlayers: [],
      searchbarSelectedTeams: [],
      currentTab: 0,
      showAddExpenseForm: false,
      expense: {},
      expensesKey: 100,
      expenseTypes: [{ id: 1, label: this.$t('expenses.transportation') }, { id: 2, label: this.$t('expenses.accommodation') }, { id: 3, label: this.$t('expenses.dietary') }, { id: 4, label: this.$t('expenses.eventTicket') }, { id: 5, label: this.$t('scoutingCalendar.tasks.type.other') }],
      showSelectExpenseDate: false,
      allTaskFiles: [],
      taskFilesToDelete: [],
      addPlayerModalKey: 0
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserLanguage() {
      return this.$store.getters["auth/language"];
    },
    filteredTaskFiles() {
      return this.allTaskFiles.length ? this.allTaskFiles.filter(f => f.typeId === 6) : [];
    },
    filteredExpenseFiles() {
      return this.allTaskFiles.length ? this.allTaskFiles.filter(f => f.typeId === 11) : [];
    },
    currencies(){
      return getCurrencies();
    },
  },
  async created() {
    //Get array of hours in interval of 15 minutes
    this.hourOptions = this.generateHours();

    //Set default values
    if (!this.isEditing) {
      //Set start & end dates
      if (this.date.date) {
        this.task.start = this.date.date;
        this.task.end = this.date.date;
      } else {
        this.task.start = moment().format('YYYY-MM-DD');
        this.task.end = moment().format('YYYY-MM-DD');
      };
      if (this.date.hasTime) {
        this.allDay = false;
        this.startHour = moment(this.date.time, 'HH:mm').format('THH:mm:ss.SSSZ');
        this.handleStartHourChange();
      };
    }; 

    //Load leagues by clientId
    await this.getLeaguesByCurrentClientId();

    //Set task values if editing
    if (this.isEditing) {
      this.task = _.cloneDeep(this.taskToUpdate); //Keep original content in a different object
      if (this.eventFiles.length) {
        if (!this.task.gameId && this.task.players.length) this.filteredFiles = this.eventFiles.filter(e => e.playerId === this.task.players[0]); //don't show duplicate files
        else if (this.task.gameId || !this.task.players.length) this.filteredFiles = this.eventFiles;
      };
      this.task.expenseFiles = [];
      this.allTaskFiles = this.taskFiles;
      this.allDay = !this.task.timed;
      this.formatEventTime(this.task);
      this.currentTab = this.task.gameId || this.task.typedMatch ? 1 : 0;
      //Prepare fields for editing if player/team in match
      if ((this.task.gameId || this.task.typedMatch) && this.task.typeId !== 3) {
        if (this.task.typedLeague) this.handleShowClientLeagueInput();
        if (this.task.leagueId && this.task.gameId) {
          this.showClientMatchInput = true;
          this.task.typedMatch = `${this.task.homeTeamName} — ${this.task.awayTeamName}`;
          this.handleMatchChange();
        };
        if (this.task.gameId && this.task.teamId) this.getMatchTeams();
        if (this.task.gameId && this.task.players.length) await this.getMatchPlayers();
      };
      //Prepare fields for editing if individual players/team
      if (!(this.task.gameId || this.task.typedMatch) && this.task.typeId === 1) {
        //Search for players in dataSet
        this.task.players.forEach(pId => {
          const player = this.dataSet.players.filter(player => player.id === pId);
          this.searchbarSelectedPlayers.push(player);
        });
        //Map to match the original object from searchbar
        this.searchbarSelectedPlayers = this.searchbarSelectedPlayers.flat().map(p => {
          p.text = p.name;
          p.value = { pos: p.pos, tId: p.tId, tName: p.tName };
          return p;
        })
      } else if (!(this.task.gameId || this.task.typedMatch) && this.task.typeId === 2) {
        //Search for team in dataSet and map to match the original object from searchbar
        this.searchbarSelectedTeams = this.dataSet.teams.filter(team => team.id === this.task.teamId).map(t => {
          t.text = t.name;
          return t;
        });
      };
    };
  },
  async mounted() {
    this.loadDataSet();
    //Set match values if creating a task from game event
    if (this.taskToCreate.name && this.$route.query.matchId) {
      this.currentTab = 1;
      this.task.name = this.taskToCreate.name;
      this.formatEventTime(this.taskToCreate);
      this.task.typeId = 1;
      this.task.leagueId = this.taskToCreate.leagueId;
      this.task.gameId = this.taskToCreate.gameId;
      if (this.task.leagueId && this.task.gameId) await this.getNextGames();
      this.task.gameId = this.taskToCreate.gameId;
      this.handleMatchChange();
      this.isMatchDate = true;
      this.handleUseMatchDate();
    };

    //Set player values if creating a task from player
    if (this.taskToCreate.text && this.$route.path.includes('player')) {
      this.task.name = this.taskToCreate.text;
      this.searchbarSelectedPlayers.push(this.taskToCreate);
      this.task.players.push(this.taskToCreate.id);

    };
  },
  methods: {
    async loadDataSet() {
      this.dataSet = await this.$store.dispatch("getDataSet");
    },
    handleCloseDialog(action) {
      if (!this.isEditing) {
        this.$emit("buttonClicked", { dialog: false, action: action });
      } else {
        this.formatEventTime(this.taskToUpdate);
        if (!_.isEqual(this.task, this.taskToUpdate)) {
          this.$emit("unsavedChanges");
          this.$refs.confirmationRemoveModal.open();
        } else {
          this.$emit("buttonClicked", { dialog: false, action: action });
        };
      };
    },
    handleDateFieldsDisplay() {
      if (this.$vuetify.breakpoint.xs) return 12;
      else return this.allDay ? 5 : 3;
    },
    generateHours() {
      //Create array of hours in intervals of 15 minutes
      const hours = [];
      const totalMinutesInDay = 24 * 60;
      for (let minute = 0; minute < totalMinutesInDay; minute += 15) {
        const currentTime = moment().startOf('day').add(minute, 'minutes');
        const formattedTime = currentTime.format('HH:mm');
        hours.push({
          value: currentTime.format(`[T]HH:mm:ss.SSSZ`),
          label: formattedTime
        });
      };
      return hours;
    },
    handleAllDayChange() {
      this.startHour = null;
      this.endHour = null;
    },
    addTwoHours(time) {
      const momentTime = moment(time, "THH:mm:ss.SSSZ");
      momentTime.add(2, 'hours');
      return momentTime.format("THH:mm:ss.SSSZ");
    },
    handleStartHourChange() {
      //Set default end hour as two hours later
      this.endHour = this.addTwoHours(this.startHour);
    },
    setAllowedEndDates(date) {
      //Disable end dates placed before start date
      const startDate = moment(this.task.start); 
      const inputDate = moment(date);
      return inputDate.isSameOrAfter(startDate, 'day');
    },
    checkEndDateError() {
      this.showSelectStartDate = false; 
      this.task.end = this.task.start;
    },
    getFormattedHour(date) {
      const momentDate = moment(date);
      const timezone = momentDate.format('Z');
      return `T${momentDate.format('HH:mm')}:00.000${timezone}`;
    },
    recognizeTimeHour() {
      if (this.task.name) {
        //Detect if there is a time expression in the name of the task
        const matchHour = this.task.name.match(/^\s*(([01]?[0-9]|2[0-3]):([0-5][0-9])|([01]?[0-9])(:([0-5][0-9]))?\s*(AM|PM))/i); 
        //If match, format to 'THH:mm:ss.x00+00:00'
        if (matchHour) {
        let dateTime = moment();
          if (matchHour[4] && matchHour[7]) { // 12-hour AM/PM format
            dateTime = moment(`${matchHour[4]}:${matchHour[6] || '00'} ${matchHour[7]}`, 'hh:mm A');
          } else { // 24-hour format
            dateTime = moment(`${matchHour[2]}:${matchHour[3]}`, 'HH:mm');
          };
          // Round minutes to the nearest quarter-hour
          const minutes = dateTime.minutes();
          dateTime.minutes(minutes < 7 ? 0 : minutes < 22 ? 15 : minutes < 37 ? 30 : minutes < 52 ? 45 : 0);
          if (minutes >= 52) dateTime.add(1, 'hours');
          this.allDay = false;
          // Format to 'THH:mm:ss.SSSZ'
          this.startHour = dateTime.format("[T]HH:mm:ss.SSSZ");
          this.handleStartHourChange();
        };
      };
    },
    formatEventTime(e) {
      //Format time values to input requirements
      if (!this.allDay) {
        this.startHour = this.getFormattedHour(e.start);
        this.endHour = this.getFormattedHour(e.end);
      };
      e.start = moment(e.start).format('YYYY-MM-DD');
      e.end = moment(e.end).format('YYYY-MM-DD');
    },
    async getLeaguesByCurrentClientId() {
      const leaguesData = await ClientService.getLeaguesOfClient(this.currentUser.clientId);
      this.clientLeagues = leaguesData.data.sort((a, b) => a.tier - b.tier);
      this.clientLeagues.unshift({ leagueId: 'typedLeague', label: this.$t('scoutingCalendar.tasks.addOther') });
    },
    onImageLoadFailure(event) {
      event.target.src = "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png";
    },
    resetScoutingDetails() {
      this.task.leagueId = null;
      this.task.typedLeague = null;
      this.task.gameId = null;
      this.task.typedMatch = null;
      this.task.players = [];
      this.task.teamId = null;
      this.task.typedPlayers = null;
      this.stadiums = [];
      this.task.stadiumId = null;
      this.task.location = null;
      this.showClientLeagueInput = false;
      this.showClientMatchInput = true;
      this.isMatchDate = false;
      this.searchbarSelectedPlayers = [];
      this.searchbarSelectedTeams = [];
    },
    async getNextGames() {
      if (!this.isEditing) {
        //Reset related fields
        this.task.gameId = null;
        this.task.typedMatch = null;
        this.task.players = [];
        this.task.typedPlayers = null;
        this.task.teamId = null;
        this.task.typedTeam = null;
        this.stadiums = [];
        this.task.stadiumId = null;
        this.task.location = null;
        this.isMatchDate = false;
        this.showClientMatchInput = false;
        this.handleUseMatchDate();
      }
      //Only call backend if selected league has an existing id
      if (this.task.leagueId && this.task.leagueId !== 'typedLeague') {
        const gamesData = await GameService.getByDateAndLeagueId( //Load games from 1 year before and after current date
          moment(this.task.start, 'YYYY-MM-DD').subtract(1, 'years').format('DD-MM-YYYY'), 
          moment(this.task.end, 'YYYY-MM-DD').add(1, 'years').format('DD-MM-YYYY'), 
          this.task.leagueId
        );
        this.nextMatchDates = gamesData.data.map(game => {
          return { id: game.gameId,
            label: `${game.homeTeamName} - ${game.awayTeamName}`,
            matchDate: game.matchDate,
            homeTeamId: game.homeTeamId,
            homeTeamName: game.homeTeamName,
            awayTeamId: game.awayTeamId,
            awayTeamName: game.awayTeamName,
            stadiumId: game.stadiumId,
            stadiumName: game.stadiumName };
        }).sort((a, b) => moment(a.matchDate, 'DD-MM-YYYY HH:mm').diff(moment(b.matchDate, 'DD-MM-YYYY HH:mm'))).reverse();
        this.task.typedLeague = null;
        if (this.nextMatchDates.length) this.nextMatchDates.unshift({ id: 'typedMatch', label: this.$t('scoutingCalendar.tasks.addOther') });
      };
      if (this.task.leagueId === 'typedLeague') this.showClientLeagueInput = true;
    },
    handleShowClientLeagueInput() {
      this.showClientLeagueInput = !this.showClientLeagueInput; 
      if (this.showClientLeagueInput) {
        this.task.leagueId = null;
        this.nextMatchDates = [];
      } else {
        this.task.leagueId = null;
        this.task.typedLeague = null;
        this.nextMatchDates = [];
      }
    },
    handleShowClientMatchInput() {
      this.showClientMatchInput = !this.showClientMatchInput; 
      if (this.showClientMatchInput) {
        this.task.gameId = null;
      } else {
        this.task.gameId = null;
        this.task.typedMatch = null;
      }
    },
    getSelectedMatch() {
      const selectedMatchArray = this.nextMatchDates.filter(game => game.id === this.task.gameId);
      this.selectedMatch = selectedMatchArray[0];
    },
    getMatchTeams() {
      this.teams = [];
      //Extract teams from selected match
      const homeTeam = { 
        id: this.selectedMatch.homeTeamId || this.task.homeTeamId, 
        name: this.selectedMatch.homeTeamName || this.task.homeTeamName 
      };
      const awayTeam = { 
        id: this.selectedMatch.awayTeamId || this.task.awayTeamId, 
        name: this.selectedMatch.awayTeamName || this.task.awayTeamName 
      };
      this.teams.push(homeTeam, awayTeam);
    },
    getPositionOrder(code) {
      switch(code) {
        case 1:
          return 1;
        case 3:
          return 2.1;
        case 2:
          return 3.1;
        case 4:
          return 5.1;
        case 15:
          return 6.1;
        case 5:
          return 6.2;
        case 16:
          return 6.3;
        case 7:
          return 7.1;
        case 6:
          return 8.1;
        case 8:
          return 11.1;
        case 10:
          return 7.2;
        case 9:
          return 10.2;
        case 11:
          return 11.2;
        case 12:
          return 7.3;
        case 14:
          return 9.1;
        case 13:
          return 11.3;
        default:
          return 0;
      };
    },
    async getMatchPlayers() {
      this.players = [];
      const playersData = await CalendarService.getPlayersByGameId(this.selectedMatch.id || this.task.gameId);
      this.players = playersData.data.map(player => {
        return { id: player.id,
          name: player.name,
          fieldPositionId: player.fieldPositionId,
          positionOrder: this.getPositionOrder(player.fieldPositionId),
          teamId: player.currentTeamId,
          teamName: player.currentTeamName };
      }).sort((a, b) => a.positionOrder - b.positionOrder);
    },
    handleMatchChange() {
      if (this.isEditing) {
        this.getMatchPlayers();
        this.getMatchTeams();
      } else {
        this.stadiums = [];
        this.teams = [];
        this.players = [];
        this.showClientMatchInput = false;
        this.getSelectedMatch();
        if (this.task.gameId && this.task.gameId !== 'typedMatch') {
          //Load teams and players
          this.getMatchPlayers();
          this.getMatchTeams();
          //Set match stadium as default selected stadium
          this.stadiums.push({ id: this.selectedMatch.stadiumId, name: this.selectedMatch.stadiumName });
          this.task.stadiumId = this.selectedMatch.stadiumId;
          this.showClientMatchInput = false;
        } else {
          this.showClientMatchInput = true;
        }
      };
    },
    handleUseMatchDate() {
      if (this.isMatchDate) {
        this.allDay = false;
        const matchMoment = moment(this.selectedMatch.matchDate, 'DD/MM/YYYY HH:mm');
        //Set match day as selected day
        this.task.start = matchMoment.format('YYYY-MM-DD');
        this.task.end = this.task.start;
        //Set match hour as selected hour
        this.startHour = matchMoment.format('[T]HH:mm:ss.SSSZ');
        this.endHour = matchMoment.add(2, 'hours').format('[T]HH:mm:ss.SSSZ');
      } else {
        const currentMoment = this.date.date ? moment(this.date.date) : moment();
        this.task.start = currentMoment.format('YYYY-MM-DD');
        this.task.end = this.task.start;
        this.startHour = null;
        this.endHour = null;
        this.allDay = true;
      }
    },
    getPositionChip(position) {
      return getChipColorByPosition(position);
    },
    getPositionCode(position) {
      return getPositionCode(this.currentUser.fieldPositionCodeId, position);
    },
    getColorByFileName(name) {
      const extension = name.slice(name.lastIndexOf('.') + 1);
      switch (extension) {
        case "svg":
        case "png":
        case "jpeg":
        case "jpg":
        case "gif":
          return "orange";
        case "doc":
        case "docx":
        case "odt":
          return "blue";
        case "pdf":
          return "red";
        case "ods":
        case "xls":
        case "xlsm":
        case "xlsx":
          return "green";
        default:
          return "primary";
      };
    },
    adjustTaskDateTime() {
      const defaultTime = 'T00:00:00.000';
      const timezone = moment().format('Z');
      if (!this.allDay) {
        // If not an all-day event, set the specific start and end times
        this.task.start = this.startHour ? `${this.task.start}${this.startHour}` : `${this.task.start}${defaultTime}${timezone}`;
        if (!this.task.end) {
          this.task.end = this.task.start; // If there's no end date, use the start date
        } else {
          this.task.end = this.endHour ? `${this.task.end}${this.endHour}` : `${this.task.end}${defaultTime}${timezone}`;
        };
      } else {
        // If it's an all-day event, don't append time
        this.task.start = moment(this.task.start).format('YYYY-MM-DD');
        this.task.end = this.task.end ? moment(this.task.end).format('YYYY-MM-DD') : this.task.start; // Use start date if end date is absent
      };
    },
    async onDownloadClick(document) {
      const filename = document.name;
      const urlResponse = await DocumentService.getGameDownloadURL(document.id, filename);
      const url = urlResponse.data;
      window.open(url);
    },
    onDeleteClick(document) {
      //Remove file from ui & save it to delete later if changes are confirmed
      this.filteredFiles = this.filteredFiles.filter(file => file.id !== document.id);
      this.filesToDelete.push(document);
      this.$forceUpdate();
    },
    onDeleteFileTaskClick(document) {
      //Remove file from ui & save it to delete later if changes are confirmed
      this.allTaskFiles = this.allTaskFiles.filter(file => file.id !== document.id);
      this.taskFilesToDelete.push(document);
      this.$forceUpdate();
    },
    async onSaveClick() {
      //Check required values
      this.nameError = false;
      this.startDateError = false;

      if (!this.task.name) this.nameError = true;
      if (!this.task.start) this.startDateError = true;

      if (!this.nameError && !this.startDateError) {
        //Clean not selected values
        if (this.task.leagueId === 'typedLeague') this.task.leagueId = null;
        if (this.task.gameId === 'typedMatch') this.task.gameId = null;

        //Include time into date value if timed
        this.adjustTaskDateTime();
        
        // Set default values
        this.task.userId = this.currentUser.id;
        this.task.userAssignedIds = this.task.userAssignedIds || null;
        this.task.visibilityId = this.task.visibilityId || 2;
        this.task.color = this.task.color || 1;
        this.task.textColor = 3;
        this.task.typeId = this.task.typeId || 3;
        this.task.timed = !this.allDay;
        this.task.locationTypeId = this.task.locationTypeId || null;
        this.task.boardStateId = this.task.boardStateId || null;
        this.task.leagueId = this.task.leagueId || null;
        this.task.typedLeague = this.task.typedLeague || null;
        this.task.gameId = this.task.gameId || null;
        this.task.typedMatch = this.task.typedMatch || null;
        this.task.teamId = this.task.teamId || null;
        this.task.typedTeam = this.task.typedTeam || null;
        this.task.players = this.task.players || null;
        this.task.typedPlayers = this.task.typedPlayers || null;
        this.task.stadiumId = this.task.stadiumId || null;
        this.task.location = this.task.location || null;
        this.task.travelId = this.task.travelId || null;
        this.task.hotelId = this.task.hotelId || null;
        this.task.ticketsId = this.task.ticketsId || null;
        this.task.alertId = this.task.alertId || null;
        this.task.notes = this.task.notes || null;

        this.isSaving = true;

        //Save in database 
        if (!this.isEditing) {
          const newEventId = await CalendarService.createEvent(this.task);
          //For each file, create a document & upload it to aws
          if (this.task.files) await this.uploadDocuments(this.task, newEventId.data);
          if (this.task.taskFiles) await this.uploadTaskDocuments(this.task, newEventId.data);
          if (this.task.expenseFiles.length) await this.uploadExpenseDocuments(this.task, newEventId.data);
          //Create alert for users assigned
          if (this.task.userAssignedIds) await this.createUserAlerts(newEventId.data);
        } else {
          if (this.task.leagueId && this.task.gameId) this.task.typedMatch = null;
          const res = await CalendarService.updateEventCalendar(this.task);
          //Delete documents from database
          if (this.filesToDelete.length) {
            if (!this.task.gameId && this.task.players.length) { //If player documents, delete the document for each player
              const docNames = new Set(this.filesToDelete.map(file => file.name));
              this.filesToDelete = this.eventFiles.filter(file => docNames.has(file.name));
            };
            for (let i = 0; i < this.filesToDelete.length; i++) {
              if (this.task.gameId) await DocumentService.deleteGameDocument(this.filesToDelete[i]);
              if (!this.task.gameId && this.task.teamId) await DocumentService.deleteTeamDocument(this.filesToDelete[i]);
              if (!this.task.gameId && this.task.players.length) await DocumentService.deleteDocument(this.filesToDelete[i]);
            };
          };
          if (this.taskFilesToDelete.length) {
            for (let i = 0; i < this.taskFilesToDelete.length; i++) {
              await DocumentService.deleteTaskDocument(this.taskFilesToDelete[i]);
            };
          };
          //For each new file, create a document & upload it to aws
          if (this.task.files) await this.uploadDocuments(this.task, this.task.id);
          if (this.task.taskFiles) await this.uploadTaskDocuments(this.task, this.task.id);
          if (this.task.expenseFiles.length) await this.uploadExpenseDocuments(this.task, this.task.id);
          //Create alert for users assigned
          if (this.task.userAssignedIds) {
            for (let i = 0; i < this.task.userAssignedIds.length; i++) {
              await userService.updateUserAlertForUpdatedTask(this.task.id);
            };
          };
        };

        //Close dialog
        this.isSaving = false;
        this.$emit("eventSaved", { eventDate: this.task.start });
        this.handleCloseDialog('save');
      }
    },
    onCancelUpdateConfirmation(confirmation) {
      if (confirmation) this.$emit("buttonClicked", { dialog: false });
      this.$refs.confirmationRemoveModal.close(); 
    },
    onResultClicked(event) {
      if (this.task.typeId === 1) {
        this.searchbarSelectedPlayers.push(event);
        this.task.players.push(event.id);
      } else if (this.task.typeId === 2) {
        this.searchbarSelectedTeams = [];
        this.searchbarSelectedTeams.push(event);
        this.task.teamId = event.id;
      }
    },
    async uploadDocuments(task, newEventId) {
      for (let file of task.files) {
        if (task.gameId) {
          await this.uploadDocumentForEntity({
            entityId: task.gameId,
            entityType: 'game',
            postMethod: DocumentService.postGameDocument,
            getUrlMethod: DocumentService.getGameUploadURL,
            file,
            taskName: task.name,
            eventCalendarId: newEventId
          });
        } else if (!task.gameId && task.teamId) {
          await this.uploadDocumentForEntity({
            entityId: task.teamId,
            entityType: 'team',
            postMethod: DocumentService.postTeamDocument,
            getUrlMethod: DocumentService.getTeamUploadURL,
            file,
            taskName: task.name,
            eventCalendarId: newEventId
          });
        } else if (!task.gameId && task.players.length) {
          for (let playerId of task.players) {
            await this.uploadDocumentForEntity({
              entityId: playerId,
              entityType: 'player',
              postMethod: DocumentService.postDocument,
              getUrlMethod: DocumentService.getUploadURL,
              file,
              taskName: task.name,
              eventCalendarId: newEventId
            });
          };
        };
      };
    },
    async uploadDocumentForEntity({ entityId, entityType, postMethod, getUrlMethod, file, taskName, eventCalendarId }) {
      const newDocument = {
        [`${entityType}Id`]: entityId,
        typeId: 1, // SCOUTING_REPORT
        name: file.name,
        notes: taskName,
        eventCalendarId: eventCalendarId
      };

      const documentResponse = await postMethod(newDocument);
      const urlResponse = await getUrlMethod(documentResponse.data, newDocument.name);
      await DocumentService.upload(urlResponse.data, file);
    },
    async uploadTaskDocuments(task, eventCalendarId) {
      for (let file of task.taskFiles) {
        const newDocument = {
          eventCalendarId: eventCalendarId,
          typeId: 6, // OTHER
          name: file.name,
          notes: task.name,
          visibilityId: task.visibilityId
        };

        const documentResponse = await DocumentService.postTaskDocument(newDocument);
        const urlResponse = await DocumentService.getTaskUploadURL(documentResponse.data, newDocument.name);
        await DocumentService.upload(urlResponse.data, file);
      };
    },
    async uploadExpenseDocuments(task, eventCalendarId) {
      for (let file of task.expenseFiles) {
        const newDocument = {
          eventCalendarId: eventCalendarId,
          typeId: 11, // EXPENSE
          expenseTypeId: file.expenseTypeId,
          expenseAmount: file.expenseAmount,
          expenseDate: file.expenseDate,
          currencyId: file.currencyId,
          name: file.name,
          notes: task.name,
          visibilityId: task.visibilityId
        };

        const documentResponse = await DocumentService.postTaskDocument(newDocument);
        const urlResponse = await DocumentService.getTaskUploadURL(documentResponse.data, newDocument.name);
        await DocumentService.upload(urlResponse.data, file.file);
      };
    },
    async createUserAlerts(newEventId) {
      for (let i = 0; i < this.task.userAssignedIds.length; i++) {
        const newAlert = {};
        newAlert.userId = this.task.userAssignedIds[i];
        newAlert.alertTypeId = 14; //EventCalendar
        newAlert.targetId = newEventId;
        newAlert.isFav = false;
        newAlert.isViewed = false;
        newAlert.taskName = this.task.name;
        newAlert.targetTs = moment(this.task.start).format('DD/MM/YYYY HH:mm');
        await userService.createUserAlert(newAlert);
      };
    },
    handleCancelExpense() {
      this.expense = {};
      this.showAddExpenseForm = false;
    },
    onAddExpenseClick() {
      if (this.$refs.expensesFormRef.validate()) {
        this.expense.expenseTypeName = this.expenseTypes.filter(e => e.id === this.expense.expenseTypeId).map(e => e.label)[0];
        this.expense.name = this.expense.file.name;
        this.task.expenseFiles.push(this.expense);
        this.expense = {};
        this.showAddExpenseForm = false;
      };
    },
    onRemoveExpenseClick(document) {
      _.remove(this.task.expenseFiles, obj => _.isEqual(obj, document));
      this.expense = {};
      this.showAddExpenseForm = false;
      this.expensesKey++;
    },
    async onDownloadTaskDocumentClick(document) {
      const filename = document.name;
      const urlResponse = await DocumentService.getTaskDownloadURL(document.id, filename);
      const url = urlResponse.data;
      window.open(url);
    },
    handleAddPlayerClosed(event) {
      if (event.id) {
        const player = { id: event.id, text: event.name, value: { pos: event.fieldPositionId, tId: event.teamId }};
        this.searchbarSelectedPlayers.push(player);
        this.task.players.push(player.id);
      };
      this.addPlayerModalKey++;
    },
  }
}

</script>


// WEBPACK FOOTER //
// src/views/scouting-calendar/ScoutingCalendarAddTask.vue